import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { RedefinirSenhaUsuarioDialog } from '@/components/RedefinirSenhaUsuarioDialog';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';


interface UserRow {
  id: string;
  email: string;
  fullName: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'OPERATOR';
  isActive: boolean;
  createdAt: string;
}

function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<UserRow[]>('/users'),
  });
}

export function UsersPage() {
  const { user: current } = useAuth();
  const { data, isLoading, isError } = useUsers();
  const isSuperAdmin = current?.role === 'SUPER_ADMIN';
  const [redefinindo, setRedefinindo] = useState<UserRow | null>(null);
  const [avisoLista, setAvisoLista] = useState<string | null>(null);

  const reenviar = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ enviado: boolean; motivo?: string }>(`/users/${id}/reenviar-convite`, {
        method: 'POST',
      }),
    onSuccess: (r, id) => {
      const alvo = data?.find((u) => u.id === id);
      setAvisoLista(
        r.enviado
          ? `Convite reenviado para ${alvo?.email ?? 'o usuário'}. O link anterior foi invalidado.`
          : `Não foi possível enviar (${r.motivo ?? 'falha no e-mail'}).`,
      );
    },
    onError: () => setAvisoLista('Não foi possível reenviar o convite.'),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Usuários</h1>
          <p className="text-sm text-muted-foreground">
            {isSuperAdmin
              ? 'Cadastre e gerencie os usuários da operação.'
              : 'Lista dos usuários cadastrados.'}
          </p>
        </div>
      </header>

      {isSuperAdmin && <CreateUserForm />}

      {avisoLista && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">{avisoLista}</p>
      )}

      <section className="rounded-lg border border-border">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {isError && <p className="p-4 text-sm text-destructive">Erro ao carregar usuários.</p>}
        {data && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">E-mail</th>
                <th className="px-4 py-2 text-left font-medium">Role</th>
                <th className="px-4 py-2 text-left font-medium">Ativo</th>
                {isSuperAdmin && <th className="px-4 py-2 text-right font-medium">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">{u.fullName}</td>
                  <td className="px-4 py-2 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-2">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-primary">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {u.isActive ? (
                      '✓'
                    ) : (
                      // Inativo aqui quase sempre significa "convidado e ainda
                      // não aceitou" — dizer só "—" faria parecer desativado.
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        aguardando convite
                      </span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td className="px-4 py-2 text-right">
                      {!u.isActive && (
                        <button
                          type="button"
                          disabled={reenviar.isPending && reenviar.variables === u.id}
                          onClick={() => reenviar.mutate(u.id)}
                          data-dica="Manda um convite novo e invalida o link anterior"
                          className="mr-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
                        >
                          {reenviar.isPending && reenviar.variables === u.id
                            ? 'Enviando…'
                            : 'Reenviar convite'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setRedefinindo(u)}
                        data-dica="Define uma senha provisória e derruba as sessões abertas"
                        className="rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                      >
                        Redefinir senha
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {redefinindo && (
        <RedefinirSenhaUsuarioDialog
          usuario={redefinindo}
          onClose={() => setRedefinindo(null)}
        />
      )}
    </div>
  );
}

function CreateUserForm() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRow['role']>('OPERATOR');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: { email: string; fullName: string; role: UserRow['role'] }) =>
      apiFetch<UserRow & { conviteEnviado?: boolean; conviteMotivo?: string }>('/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (criado) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      // Convite que nao saiu e acesso que nao existe: dizer agora evita que a
      // pessoa fique esperando um e-mail que nunca vem.
      setAviso(
        criado.conviteEnviado
          ? `Convite enviado para ${criado.email}. O link vale por 7 dias.`
          : `Usuário criado, mas o convite NÃO foi enviado (${criado.conviteMotivo ?? 'falha no e-mail'}). Resolva o e-mail e use "Reenviar convite", ou defina uma senha em "Redefinir senha".`,
      );
      setEmail('');
      setFullName('');
      setRole('OPERATOR');
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError('Já existe usuário com esse e-mail.');
      } else {
        setError('Não foi possível criar o usuário.');
      }
    },
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setAviso(null);
    mutation.mutate({ email: email.trim(), fullName: fullName.trim(), role });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-card p-4 space-y-4"
    >
      <h2 className="text-base font-medium">Novo usuário</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="cu-name">
            Nome completo
          </label>
          <input
            id="cu-name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="cu-email">
            E-mail
          </label>
          <input
            id="cu-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <p className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground md:col-span-2">
          A pessoa recebe um <b>convite por e-mail</b> e cria a própria senha — você não define
          senha para ninguém. O convite vale por 7 dias, e o acesso só passa a existir quando ela
          aceitar.
        </p>
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="cu-role">
            Role
          </label>
          <select
            id="cu-role"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRow['role'])}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="OPERATOR">Operador</option>
            <option value="ADMIN">Admin</option>
            <option value="SUPER_ADMIN">Super Admin</option>
          </select>
        </div>
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      {aviso && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-800">
          {aviso}
        </div>
      )}
      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {mutation.isPending ? 'Criando…' : 'Criar usuário'}
      </button>
    </form>
  );
}
